import { useQuery } from "@tanstack/react-query";
import { Palmtree } from "lucide-react";
import { useEffect, useState } from "react";

import { getVacacionesEquipo } from "@/lib/api";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const NOW_YEAR = new Date().getFullYear();

function fmt(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MESES[m - 1]}${y !== NOW_YEAR ? ` ${y}` : ""}`;
}

/**
 * Tile tipo KPI (junto a REA/USD/HSC) con quién está de vacaciones esta semana y
 * la siguiente. Si hay varias personas, rota mostrando una a la vez (nombre +
 * fechas). Si no hay nadie, no se muestra.
 */
export function VacacionesWidget() {
  const q = useQuery({
    queryKey: ["vacaciones-equipo"],
    queryFn: getVacacionesEquipo,
    staleTime: 5 * 60_000,
    refetchInterval: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const items = q.data ?? [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;

  const pos = idx % items.length;
  const cur = items[pos];
  const enCurso = cur.en_curso;

  const tooltip = items
    .map((v) => `${v.nombre}: ${fmt(v.fecha_inicio)} – ${fmt(v.fecha_fin)}${v.en_curso ? " (en curso)" : " (próxima)"}`)
    .join("\n");

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white max-w-[15rem]"
      title={`Vacaciones — esta semana y la siguiente\n${tooltip}`}
    >
      <Palmtree
        className={`w-3.5 h-3.5 shrink-0 ${enCurso ? "text-emerald-600" : "text-amber-500"}`}
        aria-hidden="true"
      />
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-xs font-medium text-slate-700 truncate">
          {cur.nombre}
          {items.length > 1 && (
            <span className="text-[10px] text-slate-400 ml-1 tabular-nums">
              {pos + 1}/{items.length}
            </span>
          )}
        </span>
        <span className="text-[10px] text-slate-400 tabular-nums">
          {enCurso ? "En curso" : "Próxima"} · {fmt(cur.fecha_inicio)} – {fmt(cur.fecha_fin)}
        </span>
      </div>
    </div>
  );
}
